// POM-6148 / POM-6199: pure helpers for the Split Order By Quantity dialog.
import { addDays, format, isValid, parseISO, startOfDay } from 'date-fns';

// A stepped date that walked past the end of the window would be born invalid, showing its own
// error before the user had touched it. It stops at the edge instead.
const clampToWindow = (date, latestDate) =>
  isValid(latestDate) && date > latestDate ? latestDate : date;

// fillQuantity is stored as a string and parsed as a float, so quantities are always
// compared with an epsilon rather than by exact float equality.
export const QUANTITY_EPSILON = 1e-6;

export const toNumber = (value) => parseFloat(String(value ?? '').trim());

// Quantities can be fractional, so trim the float noise a sum can introduce.
export const fmtQuantity = (value) => String(Math.round(value * 100) / 100);

// Refused at keydown by the quantity field, so the character never appears and there is
// nothing to silently undo afterwards. `.` is among them: a split is a count of what gets
// dispensed now versus later, and a fractional count of that has no meaning on the order.
const BLOCKED_QUANTITY_KEYS = ['-', '+', '.', ',', 'e', 'E'];

export const isBlockedQuantityKey = (key) => BLOCKED_QUANTITY_KEYS.includes(key);

// The net for values that never pass through a keystroke — a paste, autofill, speech input,
// a browser filling the field on its own. Nothing here is something the user typed, so
// there is no typing for the stripping to contradict.
export const parseQuantity = (value) => {
  if (!value) return '';

  return String(value).replace(/[^\d]/g, '');
};

// Blank rows count as 0, so the running totals always reflect what has been entered.
export const totalToFillLater = (splits) =>
  (splits ?? []).reduce((total, split) => {
    const quantity = toNumber(split?.quantity);

    return Number.isNaN(quantity) ? total : total + quantity;
  }, 0);

/**
 * Where a newly added row opens: a lead time past the row above it, falling back to the
 * dialog's suggested date when that row has no usable date yet. POM-6197 made the lead time
 * a suggestion rather than a bound, so this only seeds the picker — the user can move the
 * date to any day after today, up to latestDate.
 */
export const suggestedDateForNextRow = ({ splits, suggestedDate, leadTimeDays, latestDate }) => {
  const lastDate = splits?.[splits.length - 1]?.fillDate;
  const next = isValid(lastDate) ? addDays(startOfDay(lastDate), leadTimeDays) : suggestedDate;

  // Enough rows and the stepped dates would walk past the end of the window; they stop at it
  // instead, so a freshly added row is never born invalid.
  return clampToWindow(next, latestDate);
};

// POM-6254: the statuses that are still ahead of their fill date, matching the server's own
// list. A canceled or already-created row is history and must not push the next date out past
// a split that is never going to happen.
const PENDING_FILL_LATER_STATUSES = ['SCHEDULED', 'WAITING_SHIPMENT'];

/**
 * scheduledFor arrives two ways: 'yyyy-MM-dd' from listPatientOrders, and a midnight-UTC instant
 * from the split mutation the store appends optimistically. Reading the instant in the browser's
 * zone would show the day before to anyone west of UTC, and a plain date string has no zone to
 * read at all — so each is turned into a local calendar day on its own terms.
 */
const scheduledCalendarDay = (value) => {
  if (!value) return null;

  if (/^\d{4}-\d{2}-\d{2}$/.test(value)) return startOfDay(new Date(`${value}T00:00:00`));

  const parsed = parseISO(value);

  return isValid(parsed)
    ? new Date(parsed.getUTCFullYear(), parsed.getUTCMonth(), parsed.getUTCDate())
    : null;
};

/**
 * The last fill date already scheduled against a line item, or null when it has none.
 *
 * The rows come back ordered by fill date, but the store's optimistic append does not preserve
 * that, so the latest is taken by comparison rather than by position.
 */
export const latestPendingFillLaterDate = (lineItem) =>
  (lineItem?.fillLaterItems ?? [])
    .filter((item) => PENDING_FILL_LATER_STATUSES.includes(item?.status))
    .map((item) => scheduledCalendarDay(item?.scheduledFor))
    .filter(isValid)
    .reduce((latest, day) => (latest === null || day > latest ? day : latest), null);

/**
 * POM-6254: where an item's FIRST row opens.
 *
 * A lead time past whatever is already scheduled against that item, falling back to the dialog's
 * suggested date when nothing is. Splitting an order twice in one sitting stepped correctly
 * because the dialog could see its own rows; splitting it, closing, and reopening restarted at
 * today, so the second split landed before the first it was meant to follow. Per item, because
 * what is pending is a property of the item and not of the order.
 *
 * Still only a suggestion — POM-6197 made the lead time a prefill rather than a bound, so the
 * user can move this to any day after today.
 */
export const suggestedFirstRowDate = ({ lineItem, suggestedDate, leadTimeDays, latestDate }) => {
  const lastScheduled = latestPendingFillLaterDate(lineItem);

  if (!lastScheduled) return suggestedDate;

  return clampToWindow(addDays(lastScheduled, leadTimeDays), latestDate);
};

/**
 * Whether an item's splits fit is a property of that item as a whole, not of any one row:
 * split 2 can be what breaks it. Reported once per item rather than under a row's field.
 */
export const splitTotalError = ({ splits, dispenseQuantity }) => {
  // Blank rows count as 0, so what is already entered is judged on its own — a row still
  // being typed does not hide an overage the rows above it have already caused.
  const later = totalToFillLater(splits);

  if (later > dispenseQuantity + QUANTITY_EPSILON) {
    return `The splits total ${fmtQuantity(later)} but only ${fmtQuantity(
      dispenseQuantity
    )} can be dispensed. Reduce a split so at least 1 fills now.`;
  }

  // The item ships before any fill-later order exists, so something must remain on it.
  if (later >= dispenseQuantity - QUANTITY_EPSILON) {
    return `Leave some quantity to fill now — the splits total ${fmtQuantity(
      later
    )} of ${fmtQuantity(dispenseQuantity)}.`;
  }

  return undefined;
};

// How a line item is titled wherever it is named in this flow — the accordion header, the
// summary banner and the snackbar all have to call the same item the same thing.
export const describeLineItem = (lineItem) =>
  [[lineItem?.itemName, lineItem?.strength].filter(Boolean).join(' '), lineItem?.form]
    .filter(Boolean)
    .join(' · ');

/**
 * One line per item that has something scheduled, for the footer banner. Multi-item
 * submissions are read down the list, so each line names its own item rather than relying
 * on the order of the accordions above.
 */
export const buildItemSummaryLine = ({ lineItem, splits, fillsNow, dispenseQuantity }) => {
  const scheduled = (splits ?? [])
    .filter((split) => toNumber(split?.quantity) > 0 && isValid(split?.fillDate))
    .map(
      (split) =>
        `${fmtQuantity(toNumber(split.quantity))} on ${format(
          startOfDay(split.fillDate),
          'MM/dd/yyyy'
        )}`
    );

  // Nothing scheduled, or scheduled past what the item holds: the panel's alert says what
  // is wrong, and a line reading "-1 of 25 stays to fill now" would only restate it as a
  // quantity the order could never hold.
  if (!scheduled.length || fillsNow <= 0) return undefined;

  const name = lineItem?.rxNumber ? `RX ${lineItem.rxNumber}` : describeLineItem(lineItem);

  return `${name}: ${scheduled.join(', ')} — ${fmtQuantity(fillsNow)} of ${fmtQuantity(
    dispenseQuantity
  )} stays to fill now.`;
};
